import { combineLatest, Observable, debounceTime, distinctUntilChanged, map, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Injectable } from '@angular/core';

import { ActivatedRouterStateSnapshot, CurrencyService, LanguageService, ProductSearchService, RoutingService, RouterState } from '@spartacus/core';
import { ProductListComponentService, ProductListRouteParams, ViewConfig } from '@spartacus/storefront';

import { CustomProductSearchService } from './custom-product-search.service';
import { CustomSearchCriteria } from '../models';

@Injectable({
  providedIn: 'root'
})
export class CustomProductListComponentService extends ProductListComponentService {
  constructor(
    protected productSearchService: ProductSearchService,
    protected routing: RoutingService,
    protected activatedRoute: ActivatedRoute,
    protected currencyService: CurrencyService,
    protected languageService: LanguageService,
    protected router: Router,
    protected config: ViewConfig,
    protected customProductSearchService: CustomProductSearchService
  ) {
    super(productSearchService, routing, activatedRoute, currencyService, languageService, router, config);

    /**
     * Observes the route and performs a search on each route change.
     *
     * Context changes, such as language and currencies are also taken
     * into account, so that the search is performed again.
     */
    this.searchByRouting$ = combineLatest([
      this.routing.getRouterState().pipe(
        distinctUntilChanged((x, y) => {
          // router emits new value also when the anticipated `nextState` changes
          // but we want to perform search only when current url changes
          return x.state.url === y.state.url;
        })
      ),
      ...this.customSiteContext
    ]).pipe(
      debounceTime(0),
      map(([routerState, ..._context]) => (routerState as RouterState).state),
      tap((state: ActivatedRouterStateSnapshot) => {
        const criteria = this.getCriteriaFromRoute(state.params, state.queryParams);
        this.search(criteria);
      })
    );
  }

  setDefaultPageSize(pageSize: number): void {
    this.config.view = { ...this.config.view, defaultPageSize: pageSize };
  }

  /**
   * @deprecated to replace with proper search
   */
  setQuery(query: string): void {
    this.route({ query, currentPage: undefined, page: null });
  }

  /**
   * Resolves the search query from the given `ProductListRouteParams`.
   */
  protected getQueryFromRouteParams({ query, categoryCode, brandCode }: ProductListRouteParams): string {
    if (query) {
      return query;
    }
    if (categoryCode) {
      return this.RELEVANCE_ALLCATEGORIES + categoryCode + ':isMarketing:true';
    }

    // TODO: drop support for brands as they should be treated
    // similarly as any category.
    if (brandCode) {
      return this.RELEVANCE_ALLCATEGORIES + brandCode;
    }

    return undefined;
  }

  protected search(criteria: CustomSearchCriteria): void {
    const currentPage = criteria.page ? criteria.page - 1 : null;
    const pageSize = criteria.pageSize;
    const sort = criteria.sortCode;
    const page = criteria.page;
    this.customProductSearchService.search(
      criteria.query,
      // TODO: consider dropping this complex passing of cleaned object
      Object.assign({}, currentPage && { currentPage }, pageSize && { pageSize }, sort && { sort }, page && { page })
    );
  }

  /**
   * Expose the `SearchCriteria`. The search criteria are driven by the route parameters.
   *
   * This search route configuration is not yet configurable
   * (see https://github.com/SAP/spartacus/issues/7191).
   */
  protected getCriteriaFromRoute(
    routeParams: ProductListRouteParams,
    queryParams: CustomSearchCriteria
  ): CustomSearchCriteria {
    return {
      query: queryParams.query || this.getQueryFromRouteParams(routeParams),
      pageSize: queryParams.pageSize || this.config.view?.defaultPageSize,
      currentPage: queryParams.page ? queryParams.page - 1 : null,
      sortCode: queryParams.sortCode,
      page: queryParams.page
    };
  }

  /**
   * Routes to the next product listing page, using the given `queryParams`. The
   * `queryParams` support sorting, pagination and querying.
   *
   * The `queryParams` are delegated to the Angular router `NavigationExtras`.
   */
  protected route(queryParams: CustomSearchCriteria): void {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute
    });
  }

  /**
   * The site context is used to update the search query in case of a
   * changing context. The context will typically influence the search data.
   *
   * We keep this private for now, as we're likely refactoring this in the next
   * major version.
   */
  private get customSiteContext(): Observable<string>[] {
    // TODO: we should refactor this so that custom context will be taken
    // into account automatically. Ideally, we drop the specific context
    // from the constructor, and query a ContextService for all contexts.

    return [this.languageService?.getActive(), this.currencyService?.getActive()];
  }
}
